import { useAuth } from '@agentnet/auth';
import { useFeatureFlag } from '@agentnet/feature-flags';
import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Grid, InputBaseComponentProps, Tooltip, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Alert, AlertTitle } from '@material-ui/lab';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, MARKS } from '@contentful/rich-text-types';
import { updateFincenCertifierAPI } from 'api/fincen-report-api';
import { CertificationFormData } from 'features/files/fincen-reporting/types';
import { usePrivacyNoticeQuery } from 'features/help/queries/useFincenContentfulQuery';
import { FileDataContext, FileDataContextInterface } from 'hooks/FileDataContext';
import { FincenReportContext, FincenReportContextInterface } from 'hooks/FincenReportContext';
import useAsync from 'hooks/useAsync';
import { FC, FunctionComponent, ReactNode, useContext, useEffect, useState } from 'react';
import MaskedField from 'react-masked-field';
import ReactInputMask from 'react-input-mask';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import MagicWandIcon from 'ui-kit/icons/MagicWand';
import { AgentNetCheckBox } from 'ui-kit/inputs/Checkbox';
import { AgentNetDropdownSelector } from 'ui-kit/inputs';
import ObscurableField from 'ui-kit/inputs/ObscurableField';
import GoogleAutocompleteSearch from 'ui-kit/inputs/GoogleAutocompleteSearch/GoogleAutocompleteSearch';
import territories from 'ui-kit/inputs/geo-data/us-territory.json';
import canadaProvinces from 'ui-kit/inputs/geo-data/canada-provinces.json';
import mexicoStates from 'ui-kit/inputs/geo-data/mexico-states.json';
import statesData from 'ui-kit/inputs/StateSelectField/states.json';
import { AgentNetTextInput } from 'ui-kit/inputs/TextField';
import { requiresStateAndPostalCode, isUSTerritory, isUS } from '@agentnet/utils';
import { certificationFormSchema } from 'utilities/validation/schemas/fincen-certifier-schema';
import { doValidate, FieldValidationError } from 'utilities/validation/validation';
import { FeatureFlag } from 'utilities/constants/launch-darkly-flags';
import { AsyncStatus } from '../constants';
import {
  CERTIFICATION_MESSAGE_ENTRY_ID,
  COMPLIANCE_REMINDER_ENTRY_ID,
  DEFAULT_TRANSMITTER_CONTROL_CODE,
} from './constants';

// Helper function to extract text from a Contentful node recursively
const extractTextFromNode = (node: any): string => {
  if (!node) return '';
  if (node.nodeType === 'text') return node.value || '';
  if (node.content && Array.isArray(node.content)) {
    return node.content.map((child: any) => extractTextFromNode(child)).join('');
  }
  return '';
};

// Helper function to filter out the checkbox paragraph and extract checkbox label
const processCheckboxContent = (contentJson: any): { filteredContent: any; checkboxLabel: string } => {
  if (!contentJson?.content || !Array.isArray(contentJson.content)) {
    return { filteredContent: contentJson, checkboxLabel: '' };
  }

  let checkboxLabel = '';
  const filteredContent = contentJson.content.filter((node: any) => {
    const textContent = extractTextFromNode(node).toLowerCase();
    if (textContent.includes('by checking this box')) {
      // Extract the checkbox text to use as label
      checkboxLabel = extractTextFromNode(node);
      return false; // Remove from content
    }
    return true;
  });

  return {
    filteredContent: { ...contentJson, content: filteredContent },
    checkboxLabel,
  };
};

// Custom rendering options for Contentful rich text
const options = {
  renderMark: {
    [MARKS.BOLD]: (text: ReactNode) => <strong>{text}</strong>,
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_: any, children: ReactNode) => <Typography variant="body1">{children}</Typography>,
  },
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      padding: theme.spacing(2),
    },

    formField: {
      margin: theme.spacing(-1, 0, 2, 0),
    },
    formField1: {
      margin: theme.spacing(-1, 0, 2, 0),
    },
    sectionTitle: {
      color: 'var(--text-primary, #212121)',
      fontFamily: 'Inter',
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: '150%',
      marginBottom: theme.spacing(2),
    },
    checkboxLabel: {
      paddingLeft: theme.spacing(1),
      paddingTop: theme.spacing(1),
    },
    certificationText: {
      color: 'var(--text-primary, #212121)',
      fontFamily: 'Inter',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '150%',
    },
    certificationIntro: {
      marginBottom: theme.spacing(2),
    },
    certificationItem: {
      marginBottom: 0,
      paddingLeft: 0,
    },
    certificationItemContainer: {
      display: 'flex',
      alignItems: 'flex-start',
    },
    numberPrefix: {
      marginRight: theme.spacing(0),
      minWidth: '20px',
      flexShrink: 0,
    },
    certificationContent: {
      flex: 1,
    },
    checkboxContainer: {
      marginTop: '-40px',
      '& > div > .MuiTypography-root': {
        fontFamily: 'Inter',
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '150%',
      },
      '& .MuiFormControlLabel-label span': {
        color: '#AC1209',
      },
    },
    phoneFieldNoIcon: {
      '& .MuiInputAdornment-root': {
        display: 'none',
      },
    },
    warningMsg: {
      marginTop: theme.spacing(2),
      width: '100%',
      '& .MuiAlertTitle-root': {
        color: 'var(--_components-alert-warning-color, #663C00)',
        fontFamily: 'Inter',
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '150%',
        letterSpacing: '0.15px',
      },
      '& .MuiAlert-message': {
        color: 'var(--_components-alert-warning-color, #663C00)',
        fontFamily: 'Inter',
        fontSize: '12px',
        fontWeight: 400,
        lineHeight: '143%',
        letterSpacing: '0.15px',
      },
      '& .MuiAlert-message .MuiTypography-root:not(.MuiAlertTitle-root)': {
        color: 'var(--_components-alert-warning-color, #663C00)',
        fontFamily: 'Inter',
        fontSize: '12px',
        fontWeight: 400,
        lineHeight: '143%',
        letterSpacing: '0.15px',
      },
    },
  }),
);

interface CertificationItemProps {
  number: string;
  children: ReactNode;
  className?: string;
}

const CertificationItem: FC<CertificationItemProps> = ({ number, children, className }) => {
  const classes = useStyles();

  return (
    <Grid item xs={12} className={className || classes.certificationItem}>
      <Typography
        variant="body1"
        component="div"
        className={`${classes.certificationItemContainer} ${classes.certificationText}`}
      >
        <span className={classes.numberPrefix}>{number}</span>
        <span className={classes.certificationContent}>{children}</span>
      </Typography>
    </Grid>
  );
};

interface CertificationFormProps {
  onChange: (field: string, value: string | boolean) => void;
  onBatchChange?: (updates: Partial<CertificationFormData>) => void;
  onValidationChange?: (isValid: boolean) => void;
  submitted?: boolean;
  formData: CertificationFormData;
  onFormSubmissionComplete: () => void;
}

const CertificationForm: FC<CertificationFormProps> = ({
  onChange,
  onBatchChange,
  onValidationChange,
  submitted = false,
  formData,
  onFormSubmissionComplete,
}) => {
  const classes = useStyles();
  const { getAccessToken } = useAuth();
  const isTccFieldEnabled = useFeatureFlag(FeatureFlag.FINCEN_TCC_FIELD_ENABLED, false);
  const [formSubmitted, setFormSubmitted] = useState<boolean>(submitted);
  const [certificationContent, setCertificationContent] = useState<any>(null);
  const [checkboxLabel, setCheckboxLabel] = useState<string>('');
  const [complianceReminderContent, setComplianceReminderContent] = useState<any>(null);
  const [complianceReminderTitle, setComplianceReminderTitle] = useState<string>('');
  const [tccInitialized, setTccInitialized] = useState<boolean>(false);

  // Fetch certification content from Contentful
  useEffect(() => {
    const fetchCertificationContent = async () => {
      try {
        const result = await usePrivacyNoticeQuery(CERTIFICATION_MESSAGE_ENTRY_ID);
        if (!result.error && result.data?.messageText) {
          const { filteredContent, checkboxLabel: extractedLabel } = processCheckboxContent(
            result.data.messageText.json,
          );
          setCertificationContent(filteredContent);
          setCheckboxLabel(extractedLabel || result.data.title || '');
        }
      } catch (error) {
        console.error('Error fetching certification content from Contentful:', error);
      }
    };

    fetchCertificationContent();
  }, []);

  // Fetch compliance reminder content from Contentful
  useEffect(() => {
    const fetchComplianceReminder = async () => {
      try {
        const result = await usePrivacyNoticeQuery(COMPLIANCE_REMINDER_ENTRY_ID);
        if (!result.error && result.data?.messageText) {
          setComplianceReminderContent(result.data.messageText.json);
          setComplianceReminderTitle(result.data.title || '');
        }
      } catch (error) {
        console.error('Error fetching compliance reminder from Contentful:', error);
      }
    };

    fetchComplianceReminder();
  }, []);

  // Set default value for transmitterControlCode only once on initial load
  useEffect(() => {
    if (isTccFieldEnabled?.value && !tccInitialized) {
      if (!formData.transmitterControlCode || formData.transmitterControlCode.trim() === '') {
        onChange('transmitterControlCode', DEFAULT_TRANSMITTER_CONTROL_CODE);
      }
      setTccInitialized(true);
    }
  }, [isTccFieldEnabled?.value, tccInitialized]);

  // Clear transmitterControlCode when feature flag is turned OFF
  useEffect(() => {
    if (tccInitialized && !isTccFieldEnabled?.value) {
      if (formData.transmitterControlCode) {
        onChange('transmitterControlCode', '');
      }
    }
  }, [isTccFieldEnabled?.value]);

  const isFieldAutoFilled = (fieldName: string): boolean => {
    return formData.autoFilledFields?.includes(fieldName) || false;
  };

  const renderMagicWandIcon = (fieldName: string): JSX.Element | undefined => {
    const fieldValue = (formData as any)[fieldName];
    if (!fieldValue || fieldValue === '') {
      return undefined;
    }
    if (isFieldAutoFilled(fieldName)) {
      return (
        <Tooltip title="Auto-filled field">
          <MagicWandIcon style={{ fontSize: pxToRem(20), cursor: 'help' }} />
        </Tooltip>
      );
    }
    return undefined;
  };

  const fileDataCtx = useContext(FileDataContext) as FileDataContextInterface;
  const { fileData } = fileDataCtx;

  const fincenReportCtx = useContext<FincenReportContextInterface | null>(FincenReportContext);
  const {
    fincenReport,
    certifierValidationErrors = [],
    generateButtonClicked,
    setCertifierValidationErrors,
  } = fincenReportCtx ?? {};
  const { addSnackbarMessage } = useSnackBars();

  useEffect(() => {
    setFormSubmitted(submitted);
  }, [submitted]);

  // Local validation errors mirror pattern from ReportingPartyForm
  const [localValidationErrors, setLocalValidationErrors] = useState<FieldValidationError[]>([]);

  const effectiveValidationErrors =
    certifierValidationErrors && certifierValidationErrors.length > 0
      ? certifierValidationErrors
      : localValidationErrors;

  const territoryOptions = territories.map((territory) => ({
    value: territory.abbreviation,
    label: territory.name,
    name: territory.name,
  }));

  const stateOptions = statesData.map((state) => ({
    value: state.abbreviation,
    label: state.name,
    name: state.abbreviation,
  }));

  const canadaProvinceOptions = canadaProvinces.map((province) => ({
    name: province.abbreviation,
    value: province.abbreviation,
  }));

  const mexicoStateOptions = mexicoStates.map((state) => ({
    name: state.abbreviation,
    value: state.abbreviation,
  }));

  // Determine which state/province options to show based on country
  const getStateOptions = () => {
    const country = formData.officeCountry?.toUpperCase();
    if (country === 'US') {
      return stateOptions;
    } else if (isUSTerritory(country)) {
      return territoryOptions.filter((t) => t.value === country);
    } else if (country === 'CA') {
      return canadaProvinceOptions;
    } else if (country === 'MX') {
      return mexicoStateOptions;
    }
    return [];
  };

  const getStateLabel = () => {
    const country = formData.officeCountry?.toUpperCase();
    if (country === 'US' || isUSTerritory(country)) {
      return 'State/U.S. Territory';
    } else if (country === 'CA') {
      return 'Province/Territory';
    } else if (country === 'MX') {
      return 'State';
    }
    return 'State/Province';
  };

  const shouldRequireStateAndPostalCode = requiresStateAndPostalCode(formData.officeCountry);

  // Revalidate locally whenever certifier form data changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      formData.firmTaxId = formData.firmTaxId?.trim() || fincenReport?.defaultFirmTaxId || '';
      const errs = (await doValidate(formData, certificationFormSchema)) || [];
      if (!cancelled) setLocalValidationErrors(errs);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    formData.firstName,
    formData.lastName,
    formData.email,
    formData.confirmEmail,
    formData.certification,
    formData.officeName,
    formData.firmTaxId,
    formData.officeAddress1,
    formData.officeAddress2,
    formData.officeCity,
    formData.officeCountry,
    formData.officeState,
    formData.officeZip,
    formData.officePhone,
    formData.transmitterControlCode,
    formData.sdtmUserId,
  ]);

  // Auto-clear context errors when local validation passes fully
  useEffect(() => {
    if (localValidationErrors.length === 0 && certifierValidationErrors.length > 0 && setCertifierValidationErrors) {
      setCertifierValidationErrors([]);
    }
  }, [localValidationErrors, certifierValidationErrors, setCertifierValidationErrors]);

  const clearFieldError = (fieldName: string) => {
    // Prefer clearing context errors if active
    if (certifierValidationErrors.length > 0 && setCertifierValidationErrors) {
      const filtered = certifierValidationErrors.filter((e) => e.field !== fieldName);
      if (filtered.length !== certifierValidationErrors.length) setCertifierValidationErrors(filtered);
      return;
    }
    if (localValidationErrors.length > 0) {
      const filtered = localValidationErrors.filter((e) => e.field !== fieldName);
      if (filtered.length !== localValidationErrors.length) setLocalValidationErrors(filtered);
    }
  };

  const getFieldError = (fieldName: string): FieldValidationError | undefined => {
    const { autoValidationTriggered, generateButtonClicked } = fincenReportCtx ?? {};
    // Only show inline validation errors after Generate Report button is clicked
    if (!generateButtonClicked) return undefined;
    if (effectiveValidationErrors.length === 0) return undefined;
    return effectiveValidationErrors.find((err) => err.field === fieldName);
  };

  const handleFieldChange = (field: string, value: string | boolean) => {
    onChange(field, value);
    clearFieldError(field);
  };

  // Always sync context certifier errors to local validation (independent of gating)
  useEffect(() => {
    if (!setCertifierValidationErrors) return;
    if (localValidationErrors.length > 0) {
      const different =
        localValidationErrors.length !== certifierValidationErrors.length ||
        localValidationErrors.some(
          (le, i) =>
            le.field !== certifierValidationErrors[i]?.field || le.message !== certifierValidationErrors[i]?.message,
        );
      if (different) setCertifierValidationErrors(localValidationErrors);
    } else if (certifierValidationErrors.length > 0) {
      setCertifierValidationErrors([]);
    }
  }, [localValidationErrors, certifierValidationErrors, setCertifierValidationErrors]);
  const updateCertificationData = async (): Promise<any> => {
    const token = await getAccessToken();
    // Ensure default value for transmitterControlCode when feature flag is enabled
    const dataToSubmit = { ...formData };
    if (isTccFieldEnabled?.value) {
      dataToSubmit.transmitterControlCode = formData.transmitterControlCode?.trim() || DEFAULT_TRANSMITTER_CONTROL_CODE;
    } else {
      // Clear the field when flag is OFF
      dataToSubmit.transmitterControlCode = '';
    }
    return updateFincenCertifierAPI(String(fincenReport?.fincenReportId || 0), dataToSubmit, token);
  };

  const {
    execute: executeUpdateCertificationData,
    value: updateCertificationResponse,
    status: updateCertificationStatus,
    errors: updateCertificationErrors,
  } = useAsync<any>(updateCertificationData, false);

  useEffect(() => {
    if (formSubmitted) {
      executeUpdateCertificationData();
    }
  }, [formSubmitted]);

  useEffect(() => {
    if (updateCertificationStatus === AsyncStatus.SUCCESS) {
      addSnackbarMessage({
        message: 'Saving Successful',
        type: 'success',
      });
      fincenReportCtx?.reloadFincenReport?.(fileData?.fileId ?? '0');
      onFormSubmissionComplete && onFormSubmissionComplete();
    }
  }, [updateCertificationStatus, addSnackbarMessage]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={6} className={classes.formField}>
        <AgentNetTextInput
          label="First Name"
          required
          variant="outlined"
          fullWidth
          value={formData.firstName}
          onChange={(e) => handleFieldChange('firstName', e.target.value)}
          data-testid="fincen-certify-firstname-input"
          error={!!getFieldError('firstName')}
          helperText={getFieldError('firstName')?.message}
          endAdornment={renderMagicWandIcon('firstName')}
        />
      </Grid>

      <Grid item xs={6} className={classes.formField}>
        <AgentNetTextInput
          label="Last Name"
          required
          variant="outlined"
          fullWidth
          value={formData.lastName}
          onChange={(e) => handleFieldChange('lastName', e.target.value)}
          data-testid="fincen-certify-lastname-input"
          error={!!getFieldError('lastName')}
          helperText={getFieldError('lastName')?.message}
          endAdornment={renderMagicWandIcon('lastName')}
        />
      </Grid>

      <Grid item xs={6} className={classes.formField}>
        <AgentNetTextInput
          label="Email"
          required
          variant="outlined"
          fullWidth
          value={formData.email}
          onChange={(e) => handleFieldChange('email', e.target.value)}
          data-testid="fincen-certify-email-input"
          error={!!getFieldError('email')}
          helperText={getFieldError('email')?.message}
          endAdornment={renderMagicWandIcon('email')}
        />
      </Grid>

      <Grid item xs={6} className={classes.formField}>
        <AgentNetTextInput
          label="Confirm Email"
          required
          variant="outlined"
          fullWidth
          value={formData.confirmEmail}
          onChange={(e) => handleFieldChange('confirmEmail', e.target.value)}
          data-testid="fincen-certify-confirm-email-input"
          error={!!getFieldError('confirmEmail')}
          helperText={getFieldError('confirmEmail')?.message}
          endAdornment={renderMagicWandIcon('confirmEmail')}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography className={classes.sectionTitle}>Office Information</Typography>
      </Grid>

      <Grid item xs={6} className={classes.formField}>
        <AgentNetTextInput
          label="Office Name"
          required
          variant="outlined"
          fullWidth
          value={formData.officeName}
          onChange={(e) => handleFieldChange('officeName', e.target.value)}
          data-testid="fincen-certify-office-name-input"
          error={!!getFieldError('officeName')}
          helperText={getFieldError('officeName')?.message}
          endAdornment={renderMagicWandIcon('officeName')}
        />
      </Grid>

      <Grid item xs={6} className={classes.formField}>
        <AgentNetTextInput
          label="Firm Tax ID"
          required
          variant="outlined"
          fullWidth
          value={formData.firmTaxId?.trim() || fincenReport?.defaultFirmTaxId || ''}
          onChange={(e) => handleFieldChange('firmTaxId', e.target.value)}
          className={classes.phoneFieldNoIcon}
          InputProps={{
            inputComponent: ReactInputMask as unknown as FunctionComponent<InputBaseComponentProps>,
            inputProps: {
              inputComponent: ReactInputMask,
              mask: '99-9999999',
              placeholder: 'XX-XXXXXXX',
              maskChar: null,
            },
            endAdornment: renderMagicWandIcon('firmTaxId'),
          }}
          data-testid="fincen-certify-firm-tax-id-input"
          error={!!getFieldError('firmTaxId')}
          helperText={getFieldError('firmTaxId')?.message}
        />
      </Grid>

      <Grid item xs={6} style={{ position: 'relative' }}>
        <GoogleAutocompleteSearch
          label="Address 1"
          required
          variant="outlined"
          fullWidth
          value={formData.officeAddress1}
          onChange={(e) => handleFieldChange('officeAddress1', e.target.value)}
          data-testid="fincen-certify-office-address1-input"
          name="officeAddress1"
          errs={effectiveValidationErrors}
          showValidation={!!getFieldError('officeAddress1')}
          stateConstraint={isUS(formData.officeCountry) && formData.officeState ? formData.officeState : undefined}
          onPlaceSelect={(place: any) => {
            const newCountry = place?.country ?? formData.officeCountry ?? '';
            const isNewUSCountry = isUS(newCountry);

            // Build updates object with all address fields from Google selection
            const updates = {
              officeAddress1: place?.address1 ?? '',
              officeAddress2: place?.address2 ?? formData.officeAddress2 ?? '',
              officeCity: place?.city ?? '',
              officeZip: place?.zipcode ?? '',
              officeCountry: newCountry,
              // For US: use state from place; for territories: state equals territory code
              officeState: isNewUSCountry ? place?.state ?? formData.officeState ?? '' : newCountry,
            };

            // Use batch update if available, otherwise fall back to individual updates
            if (onBatchChange) {
              onBatchChange(updates);
            } else {
              Object.entries(updates).forEach(([field, value]) => {
                handleFieldChange(field, value as string);
              });
            }
          }}
        />
        {isFieldAutoFilled('officeAddress1') && formData.officeAddress1 && (
          <div style={{ position: 'absolute', right: '12px', top: '16px', pointerEvents: 'none', zIndex: 1 }}>
            <Tooltip title="Auto-filled from address autocomplete" placement="top" arrow>
              <MagicWandIcon style={{ fontSize: pxToRem(20), cursor: 'help' }} />
            </Tooltip>
          </div>
        )}
      </Grid>

      <Grid item xs={6} className={classes.formField}>
        <AgentNetTextInput
          label="Address 2"
          variant="outlined"
          fullWidth
          value={formData.officeAddress2 || ''}
          onChange={(e) => handleFieldChange('officeAddress2', e.target.value)}
          data-testid="fincen-certify-office-address2-input"
          error={!!getFieldError('officeAddress2')}
          helperText={getFieldError('officeAddress2')?.message}
          endAdornment={renderMagicWandIcon('officeAddress2')}
        />
      </Grid>

      <Grid item xs={3} className={classes.formField}>
        <AgentNetTextInput
          label="City"
          required
          variant="outlined"
          fullWidth
          value={formData.officeCity}
          onChange={(e) => handleFieldChange('officeCity', e.target.value)}
          data-testid="fincen-certify-office-city-input"
          error={!!getFieldError('officeCity')}
          helperText={getFieldError('officeCity')?.message}
          endAdornment={renderMagicWandIcon('officeCity')}
        />
      </Grid>

      <Grid item xs={3} className={classes.formField}>
        <AgentNetDropdownSelector
          label="Country/Jurisdiction"
          required
          value={formData.officeCountry || 'US'}
          menuOption={(value: string) => handleFieldChange('officeCountry', value)}
          options={territoryOptions}
          data-testid="fincen-certify-office-country-select"
          errs={effectiveValidationErrors}
          showValidation={formSubmitted}
          variant="outlined"
          fullWidth
        />
      </Grid>

      {shouldRequireStateAndPostalCode && (
        <Grid item xs={3} className={classes.formField}>
          <AgentNetDropdownSelector
            label={getStateLabel()}
            required
            options={getStateOptions()}
            variant="outlined"
            fullWidth
            value={formData.officeState}
            data-testid="fincen-certify-office-state-select"
            showValidation={!!getFieldError('officeState')}
            error={!!getFieldError('officeState')}
            defaultToFirst={false}
            menuOption={(value) => handleFieldChange('officeState', value)}
            disabled={isUSTerritory(formData.officeCountry)}
          />
          {getFieldError('officeState') && (
            <Typography variant="caption" color="error">
              {getFieldError('officeState')?.message}
            </Typography>
          )}
        </Grid>
      )}

      <Grid item xs={3} className={classes.formField}>
        <AgentNetTextInput
          label="Zip"
          required
          variant="outlined"
          fullWidth
          value={formData.officeZip}
          onChange={(e) => handleFieldChange('officeZip', e.target.value)}
          data-testid="fincen-certify-office-zip-input"
          error={!!getFieldError('officeZip')}
          helperText={getFieldError('officeZip')?.message}
          endAdornment={renderMagicWandIcon('officeZip')}
        />
      </Grid>

      <Grid item xs={3} className={classes.formField}>
        <ObscurableField
          label="Phone"
          required
          variant="outlined"
          fullWidth
          value={formData.officePhone}
          onChange={(e) => handleFieldChange('officePhone', e.target.value)}
          className={classes.phoneFieldNoIcon}
          obscurePattern={/(?!)/}
          InputProps={{
            inputComponent: MaskedField as unknown as FunctionComponent<InputBaseComponentProps>,
            inputProps: {
              mask: '999-999-9999',
              placeholder: 'XXX-XXX-XXXX',
            },
            endAdornment: renderMagicWandIcon('officePhone'),
          }}
          data-testid="fincen-certify-office-phone-input"
          error={!!getFieldError('officePhone')}
          helperText={getFieldError('officePhone')?.message}
        />
      </Grid>

      {isTccFieldEnabled?.value && (
        <Grid item xs={3} className={classes.formField}>
          <AgentNetTextInput
            label="Transmitter Control Code"
            variant="outlined"
            fullWidth
            value={formData.transmitterControlCode}
            onChange={(e) => handleFieldChange('transmitterControlCode', e.target.value)}
            data-testid="fincen-report-input-transmitter-control-code"
            error={!!getFieldError('transmitterControlCode')}
            helperText={getFieldError('transmitterControlCode')?.message}
            endAdornment={renderMagicWandIcon('transmitterControlCode')}
          />
        </Grid>
      )}

      <Grid item xs={12} className={classes.formField1}>
        <div>{certificationContent && documentToReactComponents(certificationContent, options)}</div>
      </Grid>

      <Grid item xs={12} className={classes.checkboxContainer}>
        <div className={classes.checkboxLabel}>
          <AgentNetCheckBox
            label={checkboxLabel}
            value={formData.certification}
            required
            checkHandler={(checked) => handleFieldChange('certification', checked)}
            data-testid="fincen-certify-certification-checkbox"
          />
          {getFieldError('certification') && (
            <Typography variant="caption" color="error">
              {getFieldError('certification')?.message}
            </Typography>
          )}
        </div>

        {complianceReminderContent && (
          <Alert severity="warning" className={classes.warningMsg}>
            <AlertTitle>{complianceReminderTitle}</AlertTitle>
            {documentToReactComponents(complianceReminderContent, options)}
          </Alert>
        )}
      </Grid>
    </Grid>
  );
};

export default CertificationForm;
